import { useEffect, useMemo, useState } from "react";
import { Calculator, Plus, Save, Search, Trash2 } from "lucide-react";
import QboPush from "@/components/project/QboPush";
import ChangeOrders from "@/components/project/ChangeOrders";
import EstimateNotes from "@/components/project/prestart/EstimateNotes";
import { toast } from "sonner";
import { api, errMsg, money, uid } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Empty, ErrorState, Loading, Spinner } from "@/components/States";

export const CATEGORIES = ["Demolition", "Framing", "Lumber & Framing", "Exterior & Decking", "Drywall", "Flooring", "Roofing", "Insulation", "Paint & Finishes", "Millwork", "Plumbing", "Electrical", "Furniture & Decor", "Labour", "Other"];
export const lineTotal = (it) => (Number(it.qty) || 0) * (1 + (Number(it.waste_pct) || 0) / 100) * (Number(it.unit_cost) || 0) * (1 + (Number(it.markup_pct) || 0) / 100);
export const assemblyRate = (a) => a.materials.reduce((s, m) => s + m.qty_per_unit * m.unit_cost * (1 + m.waste_pct / 100), 0);
const blank = (o = {}) => ({ id: uid(), category: "Other", description: "", qty: 1, unit: "each", unit_cost: 0, waste_pct: 0, markup_pct: 15, ...o });

function Picker({ open, onClose, onPick }) {
  const prices = useFetch(open ? "/prices" : null);
  const asm = useFetch(open ? "/assemblies" : null);
  const [q, setQ] = useState("");
  const [area, setArea] = useState({});
  const list = (prices.data || []).filter((p) => `${p.product_name} ${p.product_code}`.toLowerCase().includes(q.toLowerCase())).slice(0, 40);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle className="section-title font-normal">Add from price book</DialogTitle></DialogHeader>
        <p className="eyebrow">Assemblies (priced per sq ft)</p>
        <div className="space-y-2">
          {(asm.data || []).map((a) => (
            <div key={a.id} className="flex flex-wrap items-center gap-3 rounded-lg border p-3">
              <div className="min-w-0 flex-1"><p className="text-sm font-medium">{a.name}</p><p className="font-mono text-xs text-muted-foreground">{money(assemblyRate(a))} / sq ft</p></div>
              <Input type="number" min="1" placeholder="sq ft" className="w-24" value={area[a.id] || ""} onChange={(e) => setArea({ ...area, [a.id]: e.target.value })} data-testid={`assembly-area-${a.id}`} />
              <Button size="sm" data-testid={`add-assembly-${a.id}`} onClick={() => onPick(blank({ category: a.category, description: a.name, qty: Number(area[a.id]) || 100, unit: "sq ft", unit_cost: Number(assemblyRate(a).toFixed(4)), source: "assembly" }))}>Add</Button>
            </div>
          ))}
        </div>
        <p className="eyebrow mt-4">Supplier prices</p>
        <div className="relative"><Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search Muskoka Lumber items" value={q} onChange={(e) => setQ(e.target.value)} data-testid="pricebook-picker-search" /></div>
        {prices.loading ? <Loading rows={2} /> : prices.error ? <ErrorState message={prices.error} /> : (
          <div className="divide-y rounded-lg border">
            {list.map((p) => (
              <button key={p.id} data-testid={`pick-price-${p.id}`} className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-secondary"
                onClick={() => onPick(blank({ category: p.category, description: p.product_name, unit: p.unit_type, unit_cost: p.unit_cost, waste_pct: p.waste_pct || 0, source: p.supplier }))}>
                <span className="truncate">{p.product_name}</span><span className="shrink-0 font-mono text-xs">{money(p.unit_cost)} {p.unit_type}</span>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Cell = ({ it, k, onChange, type = "number", className = "w-20" }) => (
  <Input type={type} value={it[k]} onChange={(e) => onChange({ ...it, [k]: type === "number" ? e.target.value : e.target.value })} className={`h-8 ${className}`} data-testid={`item-${k}-${it.id}`} aria-label={k} />
);

export default function EstimateTab({ projectId }) {
  const { data, setData, error, loading, reload } = useFetch(`/projects/${projectId}/estimate`);
  const [items, setItems] = useState([]);
  const [tax, setTax] = useState(13);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState(false);
  useEffect(() => { if (data) { setItems(data.items || []); setTax(data.tax_rate ?? 13); } }, [data]);

  const update = (next) => { setItems(next); setDirty(true); };
  const totals = useMemo(() => {
    const sub = items.reduce((s, it) => s + lineTotal(it), 0);
    const byCat = items.reduce((m, it) => ({ ...m, [it.category]: (m[it.category] || 0) + lineTotal(it) }), {});
    return { sub, byCat, tax: sub * tax / 100, total: sub * (1 + tax / 100) };
  }, [items, tax]);

  const save = async () => {
    setBusy(true);
    try {
      const clean = items.map((it) => ({ ...it, qty: Number(it.qty) || 0, unit_cost: Number(it.unit_cost) || 0, waste_pct: Number(it.waste_pct) || 0, markup_pct: Number(it.markup_pct) || 0 }));
      await api.put(`/projects/${projectId}/estimate`, { items: clean, tax_rate: Number(tax), notes: "" });
      setDirty(false); toast.success("Estimate saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (loading) return <Loading rows={4} />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const set = (i) => (it) => update(items.map((x, j) => (j === i ? it : x)));

  return (
    <div className="grid gap-8 xl:grid-cols-[1fr_300px]">
      <div className="min-w-0 space-y-4">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => update([...items, blank()])} data-testid="estimator-add-item-button"><Plus className="mr-1 h-4 w-4" />Blank line</Button>
          <Button variant="outline" size="sm" onClick={() => setPicker(true)} data-testid="estimator-add-from-pricebook-button"><Calculator className="mr-1 h-4 w-4" />From price book / assembly</Button>
          <Button size="sm" className="ml-auto rounded-full" onClick={save} disabled={busy || !dirty} data-testid="estimator-save-button">{busy ? <Spinner /> : <Save className="mr-1 h-4 w-4" />}{dirty ? "Save estimate" : "Saved"}</Button>
          <QboPush projectId={projectId} pushed={data?.qbo} dirty={dirty || items.length === 0} onPushed={(qbo) => setData({ ...data, qbo })} />
        </div>
        {items.length === 0 ? <Empty icon={Calculator} title="No line items" text="Add a blank line, a Muskoka Lumber item, or an assembly like a 2x6 exterior wall." /> : (
          <div className="overflow-x-auto rounded-xl border bg-card">
            <table className="w-full text-sm" data-testid="estimate-table">
              <thead className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>{["Category", "Description", "Qty", "Unit", "Unit cost", "Waste %", "Markup %", "Total", ""].map((h) => <th key={h} className="px-3 py-3 font-medium">{h}</th>)}</tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={it.id} className="border-b last:border-0">
                    <td className="px-2 py-2"><select value={it.category} onChange={(e) => set(i)({ ...it, category: e.target.value })} className="h-8 rounded-md border bg-background px-2 text-xs" data-testid={`item-category-${it.id}`} aria-label="category">
                      {[...new Set([...CATEGORIES, it.category])].map((c) => <option key={c}>{c}</option>)}</select></td>
                    <td className="px-2 py-2"><Cell it={it} k="description" type="text" className="min-w-[200px]" onChange={set(i)} /></td>
                    <td className="px-2 py-2"><Cell it={it} k="qty" onChange={set(i)} /></td>
                    <td className="px-2 py-2"><Cell it={it} k="unit" type="text" className="w-24" onChange={set(i)} /></td>
                    <td className="px-2 py-2"><Cell it={it} k="unit_cost" className="w-24" onChange={set(i)} /></td>
                    <td className="px-2 py-2"><Cell it={it} k="waste_pct" className="w-16" onChange={set(i)} /></td>
                    <td className="px-2 py-2"><Cell it={it} k="markup_pct" className="w-16" onChange={set(i)} /></td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-mono" data-testid={`item-total-${it.id}`}>{money(lineTotal(it))}</td>
                    <td className="px-2"><Button size="icon" variant="ghost" aria-label="Remove line" onClick={() => update(items.filter((_, j) => j !== i))} data-testid={`item-remove-${it.id}`}><Trash2 className="h-4 w-4" /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <aside className="h-fit space-y-4 xl:sticky xl:top-6">
       <EstimateNotes projectId={projectId} onApplied={() => { if (dirty) toast.message("Save your estimate to see the new note line"); else reload(); }} />
       <div className="space-y-4 rounded-xl border bg-card p-6">
        <p className="eyebrow">Summary</p>
        {Object.entries(totals.byCat).map(([c, v]) => <div key={c} className="flex justify-between text-sm text-muted-foreground"><span>{c}</span><span className="font-mono">{money(v)}</span></div>)}
        <div className="flex justify-between border-t pt-3 text-sm"><span>Subtotal</span><span className="font-mono" data-testid="estimate-subtotal">{money(totals.sub)}</span></div>
        <div className="flex items-center justify-between text-sm"><span className="flex items-center gap-2">HST <Input type="number" value={tax} onChange={(e) => { setTax(e.target.value); setDirty(true); }} className="h-7 w-16" data-testid="estimate-tax-rate" aria-label="Tax rate" />%</span><span className="font-mono" data-testid="estimate-tax">{money(totals.tax)}</span></div>
        <div className="flex items-end justify-between border-t pt-4"><span className="text-sm uppercase tracking-wider text-muted-foreground">Total</span><span className="stat text-2xl" data-testid="estimate-total">{money(totals.total)}</span></div>
        <p className="text-xs text-muted-foreground">All amounts in CAD. Line total = qty × (1 + waste) × unit cost × (1 + markup).</p>
       </div>
      </aside>
      <Picker open={picker} onClose={() => setPicker(false)} onPick={(it) => { update([...items, it]); setPicker(false); toast.success("Line added"); }} />
      <ChangeOrders projectId={projectId} />
    </div>
  );
}
